import React, { useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { tagCreateToDbTagInsert } from '@/utils/mappers/tagMappers';
import { DbTagInsert } from '@/types/entities/tag';
import { paymentMethodCreateToDbInsert } from '@/utils/mappers/paymentMethodMappers';
import { DbPaymentMethodInsert } from '@/types/entities/paymentMethod';

// Minimal, idempotent defaults seeding. Runs once per user (localStorage flag)
const STORAGE_KEY_PREFIX = 'defaults_initialized_v1_';

// Проверяет, является ли ошибка конфликтом (дубликат)
function isConflictError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string; status?: number };
  if (e.code === '23505') return true; // PostgreSQL unique violation
  if (e.status === 409) return true;
  if (typeof e.message === 'string' && (e.message.includes('duplicate') || e.message.includes('409') || e.message.includes('conflict'))) return true;
  return false;
}

async function ensureTagParent(userId: string, type: DbTagInsert['type'], name: string) {
  const { data, error } = await supabaseWorker
    .from('tags')
    .select('id')
    .eq('user_id', userId)
    .eq('type', type)
    .eq('name', name)
    .is('parent_id', null)
    .limit(1)
    .maybeSingle();
  const errCode = (error as { code?: string } | null)?.code;
  if (error && errCode !== 'PGRST116') throw error;
  const existingId = (data as { id?: string } | null)?.id;
  if (existingId) return existingId;
  const insert: DbTagInsert = tagCreateToDbTagInsert({
    name,
    type,
    color: null,
    description: null,
    parentId: null,
  }, userId);
  const { data: created, error: insertError } = await supabaseWorker
    .from('tags')
    .insert(insert)
    .select('id')
    .single();
  
  // Обработка конфликта: тег уже существует (race condition)
  if (insertError && isConflictError(insertError)) {
    // Повторно запрашиваем существующий тег
    const { data: existing } = await supabaseWorker
      .from('tags')
      .select('id')
      .eq('user_id', userId)
      .eq('type', type)
      .eq('name', name)
      .is('parent_id', null)
      .limit(1)
      .maybeSingle();
    const existingIdRetry = (existing as { id?: string } | null)?.id;
    if (existingIdRetry) return existingIdRetry;
    // Если всё ещё не найден — пробрасываем ошибку
    throw insertError;
  }
  if (insertError) throw insertError;
  return (created as { id: string }).id;
}

async function ensureChildTags(userId: string, type: DbTagInsert['type'], parentId: string, names: string[]) {
  // Fetch existing children names
  const { data: existing } = await supabaseWorker
    .from('tags')
    .select('id,name')
    .eq('user_id', userId)
    .eq('type', type)
    .eq('parent_id', parentId);
  const existingNames = new Set(((existing ?? []) as Array<{ name: string }>).map(t => t.name));

  const toCreate = names.filter(n => !existingNames.has(n));
  if (toCreate.length === 0) return;

  const inserts: DbTagInsert[] = toCreate.map((name) => tagCreateToDbTagInsert({
    name,
    type,
    color: null,
    description: null,
    parentId,
  }, userId));

  const { error } = await supabaseWorker
    .from('tags')
    .insert(inserts);
  // Игнорируем ошибки конфликта (дубликаты) — теги уже существуют
  if (error && !isConflictError(error)) throw error;
}

async function ensureRootTags(userId: string, type: DbTagInsert['type'], names: string[]) {
  // Fetch existing root tags of this type
  const { data: existing } = await supabaseWorker
    .from('tags')
    .select('id,name')
    .eq('user_id', userId)
    .eq('type', type)
    .is('parent_id', null);
  const existingNames = new Set(((existing ?? []) as Array<{ name: string }>).map(t => t.name));

  const toCreate = names.filter(n => !existingNames.has(n));
  if (toCreate.length === 0) return;

  const inserts: DbTagInsert[] = toCreate.map(name => 
    tagCreateToDbTagInsert({
      name,
      type,
      color: null,
      description: null,
      parentId: null,
    }, userId)
  );

  const { error } = await supabaseWorker
    .from('tags')
    .insert(inserts);
  // Игнорируем ошибки конфликта (дубликаты) — теги уже существуют
  if (error && !isConflictError(error)) throw error;
}

async function ensurePaymentMethods(userId: string) {
  const { data: existing } = await supabaseWorker
    .from('payment_methods')
    .select('id,name,is_default')
    .eq('user_id', userId);
  const names = new Set(((existing ?? []) as Array<{ name: string }>).map(p => p.name));

  const defaults = [
    { name: 'Наличные', isDefault: true },
    { name: 'Карта', isDefault: false },
  ];

  const toCreate = defaults.filter(d => !names.has(d.name));
  if (toCreate.length === 0) return;

  const inserts: DbPaymentMethodInsert[] = toCreate.map(d => paymentMethodCreateToDbInsert({
    name: d.name,
    isActive: true,
    isDefault: d.isDefault,
  }, userId));

  // If we already have a default in DB, avoid creating another default=true
  const hasDefault = ((existing ?? []) as Array<{ is_default: boolean }>).some(p => p.is_default === true);
  if (hasDefault) inserts.forEach(i => { i.is_default = false; });

  const { error } = await supabaseWorker
    .from('payment_methods')
    .insert(inserts);
  // Игнорируем ошибки конфликта (дубликаты) — методы оплаты уже существуют
  if (error && !isConflictError(error)) throw error;
}

export const DefaultsBootstrapper: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id) return;
    const key = STORAGE_KEY_PREFIX + user.id;
    if (localStorage.getItem(key) === '1') return;

    (async () => {
      try {
        // Inventory: status and condition groups
        const invStatusParent = await ensureTagParent(user.id, 'inventory', 'Статус (Инвентарь)');
        await ensureChildTags(user.id, 'inventory', invStatusParent, ['Доступно', 'Арендовано', 'В ремонте']);

        const invConditionParent = await ensureTagParent(user.id, 'inventory', 'Состояние (Инвентарь)');
        await ensureChildTags(user.id, 'inventory', invConditionParent, ['Хорошее', 'Удовлетворительное', 'Плохое']);

        // Rentals statuses (as root tags)
        await ensureRootTags(user.id, 'rental', ['Активно', 'Завершено', 'Отменено', 'Просрочено']);

        // Orders statuses
        const orderStatusParent = await ensureTagParent(user.id, 'order', 'Статус (Заказ)');
        await ensureChildTags(user.id, 'order', orderStatusParent, ['Ожидание', 'К сборке', 'К сдаче', 'Завершено', 'Отменено']);

        // Payment status for orders (hidden parent tag for hierarchy)
        const paymentStatusParent = await ensureTagParent(user.id, 'order', 'Статус оплаты');
        await ensureChildTags(user.id, 'order', paymentStatusParent, ['Оплачено', 'Ожидание', 'Предоплата', 'Не оплачено']);

        // Store availability
        const storeAvailParent = await ensureTagParent(user.id, 'store', 'Доступность (Магазин)');
        await ensureChildTags(user.id, 'store', storeAvailParent, ['В наличии', 'Нет в наличии', 'Под заказ']);

        // Payment methods
        await ensurePaymentMethods(user.id);

        localStorage.setItem(key, '1');
        // optional console log for diagnostics
        // console.log('Defaults initialized for user', user.id);
      } catch (e) {
        // Fail silent to not block UI; can be retried next visit
        console.error('Defaults initialization error:', e);
      }
    })();
  }, [user?.id]);

  return null;
};
